import { faDownload } from '@fortawesome/free-solid-svg-icons';
import * as OvationsApi from 'core/ovations-api';
import FAIcon from 'core/ovations-components/FAIcon';
import React from 'react';

import CascadingDropdownQuestionField, { CascadingDropdownQuestionProps } from './CascadingDropdownQuestionField';
import DateTimeQuestionField, { DateTimeQuestionProps } from './DateTimeQuestionField';
import QuestionFieldProps from './definitions/QuestionFieldProps';
import DropdownQuestionField, { DropdownQuestionFieldProps } from './DropdownQuestionField';
import FileUploadQuestionField, { FileUploadQuestionProps } from './FileUploadQuestionField';
import MultiSelectQuestionField, { MultiSelectQuestionFieldProps } from './MultiSelectQuestionField';
import NumberQuestionField, { NumberQuestionProps } from './NumberQuestionField';
import TextQuestionField, { TextQuestionProps } from './TextQuestionField';

type PromotionQuestion = OvationsApi.Types.PromotionQuestion;

export const questionFieldIsDisabled = (props: QuestionFieldProps<PromotionQuestion>) => {
    if (props.readOnly) {
        return true;
    }
    const { promotionQuestion } = props;
    return props.isCallCenter ? promotionQuestion.readOnlyOnCallCenter : promotionQuestion.readOnlyOnPortal;
};

export const questionFieldIsRequired = (props: QuestionFieldProps<PromotionQuestion>): boolean => {
    const { promotionQuestion } = props;
    if (!props.isCallCenter) {
        return promotionQuestion.requiredOnPortal;
    }
    if (promotionQuestion.requiredOnCallCenter) {
        return OvationsApi.Claim.isStatusSameOrAfter(props.claimStatus, OvationsApi.Enums.ClaimStatus.Validated);
    }
    return false;
};

const renderInput = (props: QuestionFieldProps) => {
    switch (props.promotionQuestion.question.type) {
        case OvationsApi.Enums.QuestionType.Text:
        case OvationsApi.Enums.QuestionType.ReferredIndividualName:
        case OvationsApi.Enums.QuestionType.ReferredIndividualEmail:
            return <TextQuestionField {...(props as TextQuestionProps)} />;

        case OvationsApi.Enums.QuestionType.DateTime:
            return <DateTimeQuestionField {...(props as DateTimeQuestionProps)} />;

        case OvationsApi.Enums.QuestionType.Number:
            return <NumberQuestionField {...(props as NumberQuestionProps)} />;

        case OvationsApi.Enums.QuestionType.CascadingDropdown:
            return <CascadingDropdownQuestionField {...(props as CascadingDropdownQuestionProps)} />;

        case OvationsApi.Enums.QuestionType.Dropdown:
            return <DropdownQuestionField {...(props as DropdownQuestionFieldProps)} />;

        case OvationsApi.Enums.QuestionType.FileUpload:
            return <FileUploadQuestionField {...(props as FileUploadQuestionProps)} />;

        case OvationsApi.Enums.QuestionType.MultiSelect:
            return <MultiSelectQuestionField {...(props as MultiSelectQuestionFieldProps)} />;

        default:
            return null;
    }
};

const renderLabelExtras = (props: QuestionFieldProps) => {
    switch (props.promotionQuestion.question.type) {
        case OvationsApi.Enums.QuestionType.FileUpload: {
            const answer = props.answer as OvationsApi.Types.PromotionFileUploadQuestionAnswer | undefined;
            if (answer && answer.fileUrl && props.allowFileDownloads) {
                return (
                    <a
                        rel="noopener noreferrer"
                        href={`${answer.fileUrl}&forceDownload=true`}
                        target="_blank"
                        className="text-dark ms-2"
                        title="Download File"
                    >
                        <FAIcon icon={faDownload} size="sm" />
                    </a>
                );
            }
        }
    }
};

const renderQuantityRewardLimit = (props: QuestionFieldProps) => {
    const { isCallCenter, quantityRewardLimit, promotionQuestion } = props;
    if (
        !isCallCenter ||
        !quantityRewardLimit ||
        promotionQuestion.question.number !== quantityRewardLimit.questionNumber
    ) {
        return;
    }

    const total = quantityRewardLimit.total || 0;
    const used = quantityRewardLimit.used || 0;
    const remaining = total - used;

    const calculation =
        quantityRewardLimit.total == null ? (
            <>({quantityRewardLimit.used} / -)</>
        ) : (
            <>
                (Remaining: {remaining} / {total})
            </>
        );

    return <span className="text-info">{calculation}</span>;
};

const QuestionField: React.SFC<QuestionFieldProps> = (props) => {
    let id = `question_${props.promotionQuestion.question.id}`;
    if (props.isProofOfPurchase) {
        id += '_proof-of-purchase';
    }
    const inputProps = {
        id,
        ...props.inputProps,
    };
    return (
        <div className="form-group">
            <div className="label">
                <label htmlFor={inputProps.id} className="form-label d-inline fw-bold">
                    {props.promotionQuestion.question.title}
                </label>
                {renderLabelExtras(props)} {renderQuantityRewardLimit(props)}
            </div>
            {props.promotionQuestion.question.description && (
                <label htmlFor={inputProps.id} className="form-label small form-text mt-0 d-block">
                    {props.promotionQuestion.question.description}
                </label>
            )}

            {renderInput({ ...props, inputProps } as QuestionFieldProps)}

            {props.promotionQuestion.question.helpText && (
                <small
                    className="form-text text-muted"
                    // figure out why we had to do this
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: props.promotionQuestion.question.helpText }}
                />
            )}
        </div>
    );
};

export default QuestionField;
